import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../style/Product.css';
import '../style/Footer.css';
import Grid2 from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Select from 'react-select';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FlagIcon from '@mui/icons-material/Flag';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { apiCall } from '../services/apiService';
import AmazonShopButton from '../components/AmazonShopButton';
import { isProductRecentlyAdded } from '../utils/productDateUtils';

function Product() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [nutrientAverages, setNutrientAverages] = useState(null);
  const [allProducts, setAllProducts] = useState([]); // List of all products
  const [selectedProductId, setSelectedProductId] = useState(''); // ID of the selected product for comparison
  const [selectedProduct, setSelectedProduct] = useState(null); // Data of the selected product
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [ingredientsExpanded, setIngredientsExpanded] = useState(false);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [similarProductsLoading, setSimilarProductsLoading] = useState(false);
  const [similarityTab, setSimilarityTab] = useState(0);
  const [similarProductsByType, setSimilarProductsByType] = useState({
    flavor_texture: [],
    health_benefits: [],
    nutritional: [],
    brand: []
  });
  const [loadingByType, setLoadingByType] = useState({
    flavor_texture: false,
    health_benefits: false,
    nutritional: false,
    brand: false
  });
  const [asFedTooltipOpen, setAsFedTooltipOpen] = useState(false);
  const [dryMatterTooltipOpen, setDryMatterTooltipOpen] = useState(false);

  // Check if we should show score breakdown (debug mode)
  const urlParams = new URLSearchParams(window.location.search);
  const debugParam = urlParams.get('debug');
  
  // Show breakdown ONLY in development mode, never in production
  const showScoreBreakdown = process.env.NODE_ENV === 'development' && debugParam !== 'false';

  // Close tooltips when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (asFedTooltipOpen || dryMatterTooltipOpen) {
        setAsFedTooltipOpen(false);
        setDryMatterTooltipOpen(false);
      }
    };

    if (asFedTooltipOpen || dryMatterTooltipOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [asFedTooltipOpen, dryMatterTooltipOpen]);

  const productOptions = (allProducts || [])
    .map(prod => ({
      value: prod.id,
      label: `${prod.brand} - ${prod.line} - ${prod.name}`
    }))
    .sort((a, b) => a.label.localeCompare(b.label)); // Sorting by label alphabetically

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await apiCall(`api/product/${id}`);
        
        if (!response.ok) {
          throw new Error('Product not found');
        }
        
        const data = await response.json();
        setProduct(data);
      } catch (err) {
        setError(err.message);
      }
    };
    
    fetchProduct();
  }, [id]);

  // Fetch nutrient averages only if product is loaded and has a category
  useEffect(() => {
    if (product && product.category) {
      apiCall(`api/nutrient-averages/${product.category}`)
        .then(response => response.json())
        .then(data => setNutrientAverages(data))
        .catch(err => console.error('Error fetching averages:', err));
    }
  }, [product]); // This will re-run whenever `product` changes

  // Fetch list of all products for comparison
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await apiCall('api/products');
        
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        
        const data = await response.json();
        setAllProducts(data);
      } catch (err) {
        console.error('Error fetching products list:', err);
        setAllProducts([]); // Set empty array to prevent map error
      }
    };
    
    fetchProducts();
  }, []);

  // Fetch data for the selected product to compare
  useEffect(() => {
    const fetchSelectedProduct = async () => {
      if (selectedProductId) {
        try {
          const response = await apiCall(`api/product/${selectedProductId}`);
          
          if (!response.ok) {
            throw new Error('Failed to fetch selected product');
          }
          
          const data = await response.json();
          setSelectedProduct(data);
        } catch (err) {
          console.error('Error fetching selected product:', err);
        }
      }
    };
    
    fetchSelectedProduct();
  }, [selectedProductId]);

  // Fetch similar products for all types
  useEffect(() => {
    const fetchSimilarProductsByType = async (type) => {
      if (product && product.id) {
        setLoadingByType(prev => ({ ...prev, [type]: true }));
        try {
          const debugParam = showScoreBreakdown ? 'true' : 'false';
          const response = await apiCall(`api/similar-products/${product.id}?type=${type}&debug=${debugParam}`);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch ${type} similar products`);
          }
          
          const data = await response.json();
          console.log(`Debug mode: ${showScoreBreakdown}`);
          console.log(`Similar products data for ${type}:`, data.similar_products);
          if (data.similar_products && data.similar_products.length > 0) {
            console.log(`First product scoreBreakdown:`, data.similar_products[0].scoreBreakdown);
          }
          setSimilarProductsByType(prev => ({
            ...prev,
            [type]: data.similar_products || []
          }));
        } catch (err) {
          console.error(`Error fetching ${type} similar products:`, err);
          setSimilarProductsByType(prev => ({
            ...prev,
            [type]: []
          }));
        } finally {
          setLoadingByType(prev => ({ ...prev, [type]: false }));
        }
      }
    };

    if (product && product.id) {
      // Fetch all types
      if (hasFlavorTextureData()) {
        fetchSimilarProductsByType('flavor_texture');
      }
      if (hasHealthBenefits()) {
        fetchSimilarProductsByType('health_benefits');
      }
      if (hasNutritionalData()) {
        fetchSimilarProductsByType('nutritional');
      }
      fetchSimilarProductsByType('brand');
    }
  }, [product]);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle dropdown change
  const handleProductSelect = (selectedOption) => {
    setSelectedProductId(selectedOption ? selectedOption.value : "");
  };

  // Share functionality
  const handleShareClick = (event) => {
    setShareMenuAnchor(event.currentTarget);
  };

  const handleShareMenuClose = () => {
    setShareMenuAnchor(null);
  };

  const copyToClipboard = async () => {
    const currentUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(currentUrl);
      setSnackbarMessage('Link copied to clipboard!');
      setSnackbarOpen(true);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSnackbarMessage('Link copied to clipboard!');
      setSnackbarOpen(true);
    }
    handleShareMenuClose();
  };

  const shareOnWhatsApp = () => {
    const currentUrl = window.location.href;
    const productName = product ? `${product.brand} - ${product.name}` : 'Product';
    const message = `🐱 Check out this cat food on The Picky Kitty: ${productName}\n${currentUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    handleShareMenuClose();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSimilarityTabChange = (event, newValue) => {
    setSimilarityTab(newValue);
  };

  const getSimilarityTypeFromTab = (tabIndex) => {
    let types = [];
    
    // Only add flavor_texture if product has proteins or texture data
    if (hasFlavorTextureData()) {
      types.push('flavor_texture');
    }
    
    if (hasHealthBenefits()) {
      types.push('health_benefits');
    }
    
    if (hasNutritionalData()) {
      types.push('nutritional');
    }
    
    types.push('brand');
    
    return types[tabIndex];
  };

  // Check if product has nutritional data
  const hasNutritionalData = () => {
    return product && (
      product.proteinDryMatter || 
      product.phosphorusDryMatter || 
      product.sodiumDryMatter || 
      product.carbsDryMatter || 
      product.fatDryMatter
    );
  };

  // Check if product has flavor/texture data (proteins AND texture)
  const hasFlavorTextureData = () => {
    if (!product) return false;
    
    // Check if product has proteins data
    const hasProteins = Array.isArray(product.proteinsDetailed) && product.proteinsDetailed.length > 0;

    // Check if product has texture data
    const hasTexture = product.texture && product.texture.trim().length > 0;

    return hasProteins && hasTexture;
  };

  // Check if product has health benefits
  const hasHealthBenefits = () => {
    if (!product || !product.healthBenefits) {
      return false;
    }
    
    // Handle different formats: array, string, or PostgreSQL array format
    if (Array.isArray(product.healthBenefits)) {
      return product.healthBenefits.length > 0;
    }

    return false;
  };

  const getTabLabel = (tabIndex) => {
    let labels = [];
    
    // Only add flavor/texture tab if product has proteins or texture data
    if (hasFlavorTextureData()) {
      labels.push('Similar Flavor / Texture');
    }
    
    if (hasHealthBenefits()) {
      labels.push('Similar Health Benefits');
    }
    
    if (hasNutritionalData()) {
      labels.push('Similar Nutrition');
    }
    
    labels.push('Same Brand');
    
    return labels[tabIndex];
  };

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!product) {
    return (
      <div className="product-container">
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="text" width={250} />
        <Skeleton variant="rectangular" width={300} height={200} style={{ marginTop: 20 }} />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="80%" />
      </div>
    );
  }

  // Nutrients to display
  const nutrients = [
    { label: 'Moisture', key: 'moisture' },
    { label: 'Protein', key: 'protein' },
    { label: 'Phosphorus', key: 'phosphorus' },
    { label: 'Carbohydrates', key: 'carbs' },
    { label: 'Sodium', key: 'sodium' },
    { label: 'Fiber', key: 'fiber' },
    { label: 'Fat', key: 'fat' },
    { label: 'Potassium', key: 'potassium' },
    { label: 'Ash', key: 'ash' },
  ];

  const comparisonNutrients = [
    { label: 'Protein', key: 'proteinDryMatter' },
    { label: 'Phosphorus', key: 'phosphorusDryMatter' },
    { label: 'Carbs', key: 'carbsDryMatter' },
    { label: 'Sodium', key: 'sodiumDryMatter' },
    { label: 'Fiber', key: 'fiberDryMatter' },
    { label: 'Fat', key: 'fatDryMatter' },
    { label: 'Potassium', key: 'potassiumDryMatter' },
    { label: 'Ash', key: 'ashDryMatter' },
  ];

  // Helper function to format nutrient values
  const getNutrientSource = (productData, fieldKey) => productData?.nutrientSources?.[fieldKey] || null;

  const formatNutrientValue = (value, source) => {
    if (value === null || value === undefined) return "N/A";
    if (value === 0) return "0%";
    const suffix = source === 'guaranteed_analysis' ? '*' : '';
    return `${parseFloat(value).toFixed(2)}%${suffix}`;
  };

  const getNutrientTextColor = (productData, fieldKey) => (
    getNutrientSource(productData, fieldKey) === 'guaranteed_analysis' ? 'blue' : 'inherit'
  );

  // Format ingredients string
  const formatIngredients = (ingredients) => {
    if (ingredients && typeof ingredients === 'string') {
      return ingredients
        .replace(/[{}"]/g, '')  // Remove curly braces and quotation marks
        .split(',')  // Split the string by commas
        .join(', ');  // Join with commas and spaces
    }
    return 'Information not available';
  };

  // Format ingredients with truncation
  const getFormattedIngredients = (ingredients) => {
    const formatted = formatIngredients(ingredients);
    const maxLength = 200; // Show first 200 characters
    
    if (formatted.length <= maxLength || ingredientsExpanded) {
      return formatted;
    }
    
    // Find the last complete word within the limit
    const truncated = formatted.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    const finalTruncated = lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) : truncated;
    
    return finalTruncated + '...';
  };

  const isRecentlyAdded = isProductRecentlyAdded(product.createDate);

  return (
    <div className="product-container">

      <div style={{ position: 'relative', marginBottom: '20px' }}>
        {isMobile && (
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            alignItems: 'center', 
            justifyContent: 'flex-end',
            marginBottom: '5px',
            marginTop: '5px',
            marginRight: '5px'
          }}>
            <Tooltip title="Share" placement="bottom">
              <IconButton
                onClick={handleShareClick}
                sx={{
                  backgroundColor: '#f5f5f5',
                  width: '32px',
                  height: '32px',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                }}
              >
                <ShareIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Flag Issue" placement="bottom">
              <IconButton
                onClick={() => {
                  const subject = encodeURIComponent(`The Picky Kitty - Data Issue Report - ${product.brand} ${product.name}`);
                  const body = encodeURIComponent(`Product: ${product.brand} - ${product.name}\nURL: ${window.location.href}\n\nPlease describe the data issue you found:`);
                  window.open(`mailto:meow@thepickykitty.com?subject=${subject}&body=${body}`, '_blank');
                }}
                sx={{
                  backgroundColor: '#f5f5f5',
                  width: '32px',
                  height: '32px',
                  color: '#666',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                }}
              >
                <FlagIcon />
              </IconButton>
            </Tooltip>
          </div>
        )}
        <div style={{ textAlign: 'center' }}>
          <h2>{product.brand} {product.line && product.line !== product.brand ? `- ${product.line}` : ''}</h2>
          <h1>{product.name}</h1>
          {isRecentlyAdded && (
            <Chip
              label="Recently added"
              color="info"
              size="small"
              sx={{ mt: 0.5, fontWeight: 700 }}
            />
          )}
          {product.name2 && <h2>{product.name2}</h2>}
          {product.lastUpdateDate && (
            <div style={{ 
              fontSize: '0.8rem', 
              color: '#666',
              marginTop: '10px',
              textAlign: isMobile ? 'center' : 'center'
            }}>
              Last updated: {new Date(product.lastUpdateDate).toLocaleDateString()}
            </div>
          )}
        </div>
        {!isMobile && (
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            right: 0, 
            display: 'flex', 
            gap: '8px', 
            alignItems: 'center' 
          }}>
            <Tooltip title="Share" placement="bottom">
              <IconButton
                onClick={handleShareClick}
                sx={{
                  backgroundColor: '#f5f5f5',
                  width: '32px',
                  height: '32px',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                }}
              >
                <ShareIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Flag Issue" placement="bottom">
              <IconButton
                onClick={() => {
                  const subject = encodeURIComponent(`The Picky Kitty - Data Issue Report - ${product.brand} ${product.name}`);
                  const body = encodeURIComponent(`Product: ${product.brand} - ${product.name}\nURL: ${window.location.href}\n\nPlease describe the data issue you found:`);
                  window.open(`mailto:meow@thepickykitty.com?subject=${subject}&body=${body}`, '_blank');
                }}
                sx={{
                  backgroundColor: '#f5f5f5',
                  width: '32px',
                  height: '32px',
                  color: '#666',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                }}
              >
                <FlagIcon />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>

      <h3>Product overview</h3>

      <div className="product-overview">

        <div className="product-image-url-container">
          {product.cloudinaryUrl && (
            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
              <img
                src={product.cloudinaryUrl}
                alt={product.name}
                className="product-image"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '16px',
            alignItems: 'center'
          }}>
            {product.url && (
              <a 
                href={product.url} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#ffffff',
                  textDecoration: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#1976d2',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                  transition: 'all 0.3s ease',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  minHeight: '36px',
                  lineHeight: '1.2',
                  cursor: 'pointer',
                  width: 'auto',
                  boxSizing: 'border-box',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)';
                  e.target.style.boxShadow = '0 4px 12px rgba(25, 118, 210, 0.4)';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)';
                  e.target.style.boxShadow = '0 2px 8px rgba(25, 118, 210, 0.3)';
                  e.target.style.transform = 'translateY(0px)';
                }}
                onFocus={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)';
                  e.target.style.boxShadow = '0 4px 12px rgba(25, 118, 210, 0.4)';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onBlur={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)';
                  e.target.style.boxShadow = '0 2px 8px rgba(25, 118, 210, 0.3)';
                  e.target.style.transform = 'translateY(0px)';
                }}
              >
                View on {product.brand} Website
              </a>
            )}
            
            {/* Amazon Affiliate Link */}
            {product.amazonUrl && (
              <div style={{ textAlign: 'center' }}>
                <AmazonShopButton 
                  amazonUrl={product.amazonUrl} 
                  size="small" 
                  fullWidth={false}
                />
              </div>
            )}
          </div>
        </div>


        <div className="product-details">

          <Grid2 item xs={8}>
            <Grid2 container rowSpacing={2} columnSpacing={6}>
              {/* Brand Filter */}
              <Grid2 item xs={3} md={2}>
                <Typography variant="subtitle2">Category</Typography>
                <Typography variant="body1">{product.category}</Typography>
              </Grid2>

              <Grid2 item xs={3} md={2}>
                <Typography variant="subtitle2">Main Proteins</Typography>
                <Typography variant="body1">
                  {Array.isArray(product.proteinsDetailed) && product.proteinsDetailed.length > 0
                    ? product.proteinsDetailed.join(', ')
                    : 'N/A'}
                </Typography>
              </Grid2>

              <Grid2 item xs={3} md={2}>
                <Typography variant="subtitle2">Lifestage</Typography>
                <Typography variant="body1">
                  {Array.isArray(product.lifestage) && product.lifestage.length > 0
                    ? product.lifestage.join(', ')
                    : 'N/A'}
                </Typography>
              </Grid2>

              <Grid2 item xs={3} md={2}>
                <Typography variant="subtitle2">Texture</Typography>
                <Typography variant="body1">{product.texture || 'N/A'}</Typography>
              </Grid2>

              <Grid2 item xs={3} md={2}>
                <Typography variant="subtitle2">Format</Typography>
                <Typography variant="body1">
                  {Array.isArray(product.format) && product.format.length > 0
                    ? product.format.join(', ')
                    : 'N/A'}
                </Typography>
              </Grid2>

              {/* <Grid2 item xs={3} md={2}>
                <Typography variant="subtitle2">Health Benefits</Typography>
                <Typography variant="body1">
                  {product.healthBenefits && product.healthBenefits.replace(/[{}]/g, '').trim()
                    ? product.healthBenefits
                      .replace(/[{}]/g, '')
                      .split(',')
                      .map(benefit => benefit.trim()) // Clean individual items
                      .filter(Boolean) // Remove empty values
                      .join(', ')
                    : 'N/A'}
                </Typography>
              </Grid2> */}

              <Grid2 item xs={6} md={4}>
                <Typography variant="subtitle2">Calories</Typography>
                <Typography variant="body1">{product.caloriesPerKg ? parseInt(product.caloriesPerKg) + " kcal per kg" : 'N/A'}</Typography>
              </Grid2>

              {/* Product Variants Subsection */}
              {product.variants && product.variants.length > 0 && (
                <Grid2 item xs={12} md={12}>
                  <Typography variant="subtitle2">Exists in</Typography>
                  <Typography variant="body1">
                    {product.variants.map((variant, index) => (
                      <span key={variant.id}>
                        {variant.sizeName}{variant.format ? ` ${variant.format.toLowerCase()}` : ''}
                        {index < product.variants.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </Typography>
                </Grid2>
              )}

              {
                ((
                  Array.isArray(product.healthBenefits) && product.healthBenefits.length > 0
                ) ||
                  product.containsRawFood ||
                  product.isGrainFree ||
                  product.isGmoFree ||
                  product.isLid ||
                  product.isPrescription ||
                  product.isAafco) && (
                  <Grid2 item xs={12} md={8}>
                    <Typography variant="subtitle2">Features</Typography>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {[
                        Array.isArray(product.healthBenefits) && product.healthBenefits.length > 0
                          ? `Helps with ${product.healthBenefits.join(', ')}`
                          : null,
                        product.isLid && "LID (Limited Ingredient Diet)",
                        product.containsRawFood && "Contains Raw Food",
                        product.isGrainFree && "Grain-Free",
                        product.isGmoFree && "No GMO",
                        product.isPrescription && "Requires a prescription",
                        product.isAafco && "Meets AAFCO standards"
                      ]
                        .filter(Boolean)
                        .map((feature, index) => (
                          <li key={index} style={{ marginBottom: '5px' }}>
                            {feature === "Meets AAFCO standards" ? (
                              <span>✓ {feature}</span>
                            ) : (
                              feature
                            )}
                          </li>
                        )) || <li>N/A</li>}
                    </ul>
                  </Grid2>
                )
              }


              <Grid2 item xs={16} md={12}>
                <Typography variant="subtitle2">Ingredients</Typography>
                <Typography variant="body1"> {getFormattedIngredients(product.ingredients)}</Typography>
                {formatIngredients(product.ingredients).length > 200 && (
                  <Button
                    onClick={() => setIngredientsExpanded(!ingredientsExpanded)}
                    sx={{
                      mt: 0.5,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      color: '#666',
                      padding: '4px 8px',
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    {ingredientsExpanded ? 'Show less' : 'Show more'}
                  </Button>
                )}
              </Grid2>
            </Grid2>
          </Grid2>

        </div>

      </div>

      <h3>Nutritional Analysis</h3>
      <div className="product-nutrients">
        <table className="nutrient-table">
          <thead>
            <tr>
              <th>Nutrient</th>
              <th>
                <Tooltip 
                  title={
                    <div style={{ maxWidth: '300px' }}>
                      <strong>As-Fed Values:</strong><br />
                      <br />
                      Nutrient values as they appear on the product label, including moisture content. These are the raw values before any conversion.
                    </div>
                  }
                  placement="top"
                  arrow
                  open={asFedTooltipOpen}
                  onClose={() => setAsFedTooltipOpen(false)}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#424242',
                        color: '#ffffff',
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                        maxWidth: '300px',
                        border: '1px solid #666666',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
                      }
                    }
                  }}
                >
                  <div 
                    style={{ cursor: 'pointer', textAlign: 'center' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAsFedTooltipOpen(!asFedTooltipOpen);
                    }}
                  >
                    As Fed <InfoIcon sx={{ fontSize: 14, color: '#666666', opacity: 0.8, verticalAlign: 'middle', ml: 0.5 }} />
                  </div>
                </Tooltip>
              </th>
              <th>
                <Tooltip 
                  title={
                    <div style={{ maxWidth: '300px' }}>
                      <strong>Dry Matter Basis:</strong><br />
                      <br />
                      Nutrient percentages calculated after removing all moisture content. This allows for accurate comparison between wet and dry foods.
                    </div>
                  }
                  placement="top"
                  arrow
                  open={dryMatterTooltipOpen}
                  onClose={() => setDryMatterTooltipOpen(false)}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#424242',
                        color: '#ffffff',
                        fontSize: '0.875rem',
                        lineHeight: 1.4,
                        maxWidth: '300px',
                        border: '1px solid #666666',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
                      }
                    }
                  }}
                >
                  <div 
                    style={{ cursor: 'pointer', textAlign: 'center' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDryMatterTooltipOpen(!dryMatterTooltipOpen);
                    }}
                  >
                    Dry Matter <InfoIcon sx={{ fontSize: 14, color: '#666666', opacity: 0.8, verticalAlign: 'middle', ml: 0.5 }} />
                  </div>
                </Tooltip>
              </th>
              <th>Avg. Dry Matter ({product.category} food)</th>
            </tr>
          </thead>
          <tbody>
            {nutrients.map(nutrient => (
              <tr key={nutrient.key}>
                <td>{nutrient.label}</td>
                <td style={{
                  color: getNutrientSource(product, `${nutrient.key}AsFed`) === 'guaranteed_analysis'
                    ? 'blue'
                    : 'inherit'
                }}>
                  {formatNutrientValue(
                    product[`${nutrient.key}AsFed`],
                    getNutrientSource(product, `${nutrient.key}AsFed`)
                  )}
                </td>
                <td style={{
                  color: getNutrientSource(product, `${nutrient.key}DryMatter`) === 'guaranteed_analysis'
                    ? 'blue'
                    : 'inherit'
                }}>
                  {formatNutrientValue(
                    product[`${nutrient.key}DryMatter`],
                    getNutrientSource(product, `${nutrient.key}DryMatter`)
                  )}
                </td>
                <td className="averages-column">
                  {nutrientAverages
                    ? formatNutrientValue(nutrientAverages[nutrient.key])
                    : 'Loading...'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Only show notes if there are colored values in the nutritional analysis */}
        {Object.values(product.nutrientSources || {}).includes('guaranteed_analysis') && (
          <Box sx={{ 
            mt: 3, 
            mb: 3, 
            mx: 1.5,
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 1.2 
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontStyle: 'italic',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textAlign: 'center'
              }}
            >
              <span style={{ color: 'blue' }}>*</span>
              Nutrient values displayed in blue indicate that the data come from guaranteed analysis and is not fully accurate
            </Typography>
          </Box>
        )}
      </div>

      {/* Product Comparison Section */}
      <h3>Compare with Another Product</h3>
      <div className="product-comparison">
        <Box>
          <Select className="product-selection"
            options={productOptions}
            onChange={handleProductSelect}
            value={productOptions.find(option => option.value === selectedProductId)}
            placeholder="Choose a product to compare"
            isSearchable
          />
        </Box>

        {selectedProduct ? (
          <table className="comparison-table">
            <thead>
              <tr>
                <th></th>
                <th>{product.brand} - {product.line} - {product.name}</th>
                <th>{selectedProduct.brand} - {selectedProduct.line} - {selectedProduct.name}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Image</td>
                <td>
                    <img 
                      src={product.cloudinaryUrl || '/product-placeholder.png'} 
                      alt={`${product.brand} - ${product.line} - ${product.name}`} 
                      style={{ 
                        width: product.cloudinaryUrl ? '150px' : '100px', 
                        height: 'auto' 
                      }} 
                      onError={(e) => {
                        e.target.src = '/product-placeholder.png';
                        e.target.style.width = '100px';
                      }}
                    />
                </td>
                <td>
                    <img 
                      src={selectedProduct.cloudinaryUrl || '/product-placeholder.png'} 
                      alt={`${selectedProduct.brand} - ${selectedProduct.line} - ${selectedProduct.name}`} 
                      style={{ 
                        width: selectedProduct.cloudinaryUrl ? '150px' : '100px', 
                        height: 'auto' 
                      }} 
                      onError={(e) => {
                        e.target.src = '/product-placeholder.png';
                        e.target.style.width = '100px';
                      }}
                    />
                </td>
              </tr>
              <tr>
                <td>Category</td>
                <td>{product.category}</td>
                <td>{selectedProduct.category}</td>
              </tr>
              <tr>
                <td>Texture</td>
                <td>{product.texture || 'N/A'}</td>
                <td>{selectedProduct.texture || 'N/A'}</td>
              </tr>
              <tr>
                <td>Main Proteins</td>
                <td>{Array.isArray(product.proteinsDetailed) && product.proteinsDetailed.length > 0
                  ? product.proteinsDetailed.join(', ')
                  : 'N/A'}</td>
                <td>{Array.isArray(selectedProduct.proteinsDetailed) && selectedProduct.proteinsDetailed.length > 0
                  ? selectedProduct.proteinsDetailed.join(', ')
                  : 'N/A'}</td>
              </tr>
              <tr>
                <td>Format</td>
                <td>{Array.isArray(product.format) && product.format.length > 0
                  ? product.format.join(', ')
                  : 'N/A'}</td>
                <td>{Array.isArray(selectedProduct.format) && selectedProduct.format.length > 0
                  ? selectedProduct.format.join(', ')
                  : 'N/A'}</td>
              </tr>
              <tr>
                <td>Exists in</td>
                <td>{product.variants && product.variants.length > 0
                  ? product.variants.map((variant, index) => (
                      <span key={variant.id}>
                        {variant.sizeName}{variant.format ? ` ${variant.format.toLowerCase()}` : ''}
                        {index < product.variants.length - 1 ? ', ' : ''}
                      </span>
                    ))
                  : 'N/A'}</td>
                <td>{selectedProduct.variants && selectedProduct.variants.length > 0
                  ? selectedProduct.variants.map((variant, index) => (
                      <span key={variant.id}>
                        {variant.sizeName}{variant.format ? ` ${variant.format.toLowerCase()}` : ''}
                        {index < selectedProduct.variants.length - 1 ? ', ' : ''}
                      </span>
                    ))
                  : 'N/A'}</td>
              </tr>
              <tr>
                <td>Meets AAFCO</td>
                <td>{product.isAafco ? '✓' : '✗'}</td>
                <td>{selectedProduct.isAafco ? '✓' : '✗'}</td>
              </tr>
              <tr>
                <td>Grain-Free</td>
                <td>{product.isGrainFree ? '✓' : '✗'}</td>
                <td>{selectedProduct.isGrainFree ? '✓' : '✗'}</td>
              </tr>
              <tr>
                <td>GMO-free</td>
                <td>{product.isGmoFree ? '✓' : '✗'}</td>
                <td>{selectedProduct.isGmoFree ? '✓' : '✗'}</td>
              </tr>
              <tr>
                <td>Limited Ingredients</td>
                <td>{product.isLid ? '✓' : '✗'}</td>
                <td>{selectedProduct.isLid ? '✓' : '✗'}</td>
              </tr>
              <tr>
                <td>Raw food</td>
                <td>{product.containsRawFood ? '✓' : '✗'}</td>
                <td>{selectedProduct.containsRawFood ? '✓' : '✗'}</td>
              </tr>
              <tr>
                <td>Prescription</td>
                <td>{product.isPrescription ? '✓' : '✗'}</td>
                <td>{selectedProduct.isPrescription ? '✓' : '✗'}</td>
              </tr>
              <tr>
                <td>Calories content</td>
                <td>{product.caloriesPerKg
                  ? parseInt(product.caloriesPerKg) + ' kcal/kg'
                  : 'N/A'}</td>
                <td>{selectedProduct.caloriesPerKg
                  ? parseInt(selectedProduct.caloriesPerKg) + ' kcal/kg'
                  : 'N/A'}</td>
              </tr>
              {comparisonNutrients.map((nutrientRow) => (
                <tr key={nutrientRow.key}>
                  <td>{nutrientRow.label}</td>
                  <td style={{ color: getNutrientTextColor(product, nutrientRow.key) }}>
                    {formatNutrientValue(
                      product[nutrientRow.key],
                      getNutrientSource(product, nutrientRow.key)
                    )}
                  </td>
                  <td style={{ color: getNutrientTextColor(selectedProduct, nutrientRow.key) }}>
                    {formatNutrientValue(
                      selectedProduct[nutrientRow.key],
                      getNutrientSource(selectedProduct, nutrientRow.key)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Box mt={2}>
            <Skeleton variant="rectangular" height={200} />
          </Box>
        )}

        {/* Only show notes under comparison table when a product is selected */}
        {selectedProduct && (
          <Box sx={{ 
            mt: 3, 
            mb: 3, 
            mx: 1.5,
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 1.2 
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontStyle: 'italic',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textAlign: 'center'
              }}
            >
              <span style={{ color: 'blue' }}>*</span>
              Nutrient values displayed in blue indicate that the data come from guaranteed analysis and is not fully accurate
            </Typography>
          </Box>
        )}
      </div>

      {/* Similar Products Section */}
      <h3>Similar Products</h3>
      <div className="similar-products-section" style={{
        width: isMobile ? '95%' : '100%',
        margin: '0 auto',
      }}>
        <Tabs 
          value={similarityTab} 
          onChange={handleSimilarityTabChange}
          variant="fullWidth"
          sx={{ 
            mb: 2,
            width: '100%',
            '& .MuiTab-root': {
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              minHeight: '40px'
            }
          }}
        >
          {hasFlavorTextureData() && <Tab label="Similar Flavor / Texture" />}
          {hasHealthBenefits() && <Tab label="Similar Health Benefits" />}
          {hasNutritionalData() && <Tab label="Similar Nutrition" />}
          <Tab label="Same Brand" />
        </Tabs>

        {(() => {
          const currentType = getSimilarityTypeFromTab(similarityTab);
          const currentProducts = similarProductsByType[currentType];
          const isLoading = loadingByType[currentType];

          if (isLoading) {
            return (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: isMobile ? '10px' : '20px',
                marginTop: '16px',
                width: '100%'
              }}>
                {[...Array(8)].map((_, index) => (
                  <Skeleton 
                    key={index} 
                    variant="rectangular" 
                    width="100%" 
                    height={280} 
                    style={{ borderRadius: '8px' }}
                  />
                ))}
              </div>
            );
          }

          if (currentProducts.length > 0) {
            // Limit products based on screen size: 10 for desktop, 9 for mobile
            const maxProducts = isMobile ? 9 : 10;
            const limitedProducts = currentProducts.slice(0, maxProducts);
            
            return (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: isMobile ? '10px' : '20px',
                marginTop: '16px',
                width: '100%'
              }}>
                {limitedProducts.map((similarProduct) => (
                  <div key={similarProduct.id}>
                    <div 
                      className="product-card"
                      onClick={() => window.location.href = `/product/${similarProduct.id}`}
                    >
                      <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                        <img
                          src={similarProduct.cloudinaryUrl || '/product-placeholder.png'}
                          alt={`${similarProduct.brand} - ${similarProduct.name}`}
                          onError={(e) => {
                            e.target.src = '/product-placeholder.png';
                          }}
                        />
                      </div>
                      
                      <div className="brand">
                        {similarProduct.brand}
                      </div>
                      
                      <div className="name">
                        {similarProduct.name}
                      </div>
                      
                      {/* Show total score prominently in debug mode */}
                      {showScoreBreakdown && similarProduct.similarityScore && (
                        <div style={{ 
                          textAlign: 'center', 
                          marginTop: '8px',
                          padding: '4px 8px',
                          backgroundColor: '#f0f8ff',
                          borderRadius: '4px',
                          border: '1px solid #007acc'
                        }}>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            Score: {similarProduct.similarityScore}
                          </Typography>
                        </div>
                      )}
                      
                      {similarProduct.category && (
                        <div className="category">
                          {similarProduct.category}
                        </div>
                      )}
                      
                      {Array.isArray(similarProduct.proteinsDetailed) && similarProduct.proteinsDetailed.length > 0 && (
                        <div className="proteins">
                          {similarProduct.proteinsDetailed.join(', ')}
                        </div>
                      )}
                      
                      {Array.isArray(similarProduct.healthBenefits) && similarProduct.healthBenefits.length > 0 && (
                        <div className="health-benefits">
                          {similarProduct.healthBenefits.join(', ')}
                        </div>
                      )}
                      
                      
                      {/* Spacer to fill remaining space */}
                      <div style={{ flex: 1 }}></div>
                    </div>
                    
                    {/* Score Breakdown Accordion - Only show in debug mode */}
                    {console.log(`Rendering product ${similarProduct.id}, showScoreBreakdown: ${showScoreBreakdown}, has scoreBreakdown: ${!!similarProduct.scoreBreakdown}`)}
                    {showScoreBreakdown && similarProduct.scoreBreakdown && (
                      <Accordion sx={{ mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <Typography variant="body2" color="text.secondary">
                              Score Breakdown
                            </Typography>
                            <Typography variant="h6" color="primary" fontWeight="bold">
                              Total Score: {similarProduct.similarityScore}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {Object.entries(similarProduct.scoreBreakdown).map(([key, value]) => (
                              <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {value}
                                  </Typography>
                                  <Chip 
                                    label={value} 
                                    size="small" 
                                    color={value > 0 ? "success" : value < 0 ? "error" : "default"}
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </div>
                ))}
              </div>
            );
          }

          return (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              color: '#666',
              fontStyle: 'italic'
            }}>
              No {getTabLabel(similarityTab).toLowerCase()} products found at the moment.
            </div>
          );
        })()}
      </div>

      {/* Share Menu */}
      <Menu
        anchorEl={shareMenuAnchor}
        open={Boolean(shareMenuAnchor)}
        onClose={handleShareMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disableScrollLock
      >
        <MenuItem onClick={copyToClipboard}>
          <ContentCopyIcon sx={{ mr: 1 }} />
          Copy Link
        </MenuItem>
        <MenuItem onClick={shareOnWhatsApp}>
          <WhatsAppIcon sx={{ mr: 1 }} />
          Share on WhatsApp
        </MenuItem>
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Amazon Associate Disclaimer - Only show if product has Amazon URL */}
      {product.amazonUrl && (
        <div className="product-amazon-disclaimer">
          <p>
            As an Amazon Associate, The Picky Kitty earns from qualifying purchases.{" "}
            <a href="/amazon-disclosure">
              Learn more
            </a>
          </p>
        </div>
      )}

    </div>
  );
}

export default Product;
